// Modular assessment flow — replaces the previous single-page wizard.
// Backend returns a session with multiple modules; each module has its
// own schema of questions. Frontend walks through one question at a time
// on mobile, saves aggressively, and allows save-and-exit from any step.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Spinner } from "@/components/Loader";
import { Eyebrow, Headline } from "@/components/Editorial";
import ProgressBar from "@/components/ProgressBar";
import QuestionRenderer, { isAnswerValid, evaluateConditional } from "@/components/assessment/QuestionRenderer";
import { apiGet, apiPost } from "@/lib/api";
import { getContract } from "@/lib/questionTypesContract";
import { toast } from "@/components/Toast";
import { fmtMoney } from "@/lib/format";
import { C } from "@/lib/brand";

export default function AssessmentFlow() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchSession = async () => {
    try {
      // Force fresh — BE resnapshots the module schema on resume, so any
      // intermediate cache (browser back/forward, CDN edge, service worker)
      // would otherwise hand back the pre-resnap payload and leave members
      // looking at stale question types.
      const s = await apiGet(`/consumer/assessment/sessions/${sessionId}`, {
        headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
      });
      setSession(s || null);
      setError(null);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    if (!sessionId) return;
    // Boot the question-types contract once per page lifetime. The
    // dispatcher and validators consult it synchronously; fire-and-forget
    // is fine because they fall back to the hardcoded set otherwise.
    getContract();
    fetchSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (error) {
    return (
      <ErrorScreen
        onRetry={() => { setError(null); fetchSession(); }}
        onHome={() => navigate("/app")}
      />
    );
  }
  if (!session) {
    return <div style={{ padding: 64, textAlign: "center", background: C.cream, minHeight: "100vh" }}><Spinner /></div>;
  }

  // Payment gate: every session created under the Feb 2026 pricing
  // contract must clear /app/checkout/:id before the assessment runner
  // is allowed to render. Members who started a session, bailed at
  // Stripe, then came back via a bookmark or fresh login would
  // otherwise walk through every question for free and hit a $0
  // submit screen. Detect "unpaid" by the absence of every known
  // payment signal — same envelope SubmitScreen uses. Sessions the BE
  // has already marked submitted/under-review skip the gate (no point
  // sending them back to Stripe after review started).
  const isPaid =
    session.paymentReceived === true ||
    !!session.feeReceipt ||
    session.paymentStatus === "paid" ||
    session.paymentStatus === "succeeded" ||
    session.feeStatus === "paid";
  const isPostSubmit =
    session.status === "submitted" ||
    session.status === "under_review" ||
    !!session.submittedAt;
  if (!isPaid && !isPostSubmit) {
    return (
      <PaymentGate
        sessionId={sessionId}
        onContinue={() => navigate(`/app/checkout/${sessionId}`)}
        onHome={() => navigate("/app")}
      />
    );
  }

  const modules = session.modules || [];
  const currentModule = modules.find((m) => m.status !== "completed") || null;

  // New BE progress envelope (Feb 2026 contract) — when present, drives
  // the progress indicator + section labels. Falls back to deriving
  // counts from session.modules so older sessions / pre-redeploy
  // environments keep working.
  const progress = session.progress || null;
  const pathType = progress?.pathType || session.pathType || null;
  // Legacy migration notice — surfaced as a one-time banner when a
  // member's prior session referenced a sunsetted program.
  const legacyMigrationFlag = progress?.legacyMigrationFlag || session.legacyMigrationFlag || null;
  // Coming-soon goals the member selected (cognitive / longevity /
  // vitality) — physician will discuss in review. Surface as a
  // soft callout on the FIRST module of the assessment-led flow.
  const discoveryInterestFlags = progress?.discoveryInterestFlags || session.discoveryInterestFlags || [];

  // Pre-submit surface when every module is complete.
  if (!currentModule) {
    return (
      <SubmitScreen
        session={session}
        onSubmitted={async () => {
          await fetchSession();
          navigate(`/app/assessment/${sessionId}/submitted`, { replace: true });
        }}
      />
    );
  }

  return (
    <ModuleRunner
      // Keying on moduleKey forces a clean remount when the active
      // module changes (i.e. after the previous module completes). Without
      // this, ModuleRunner reuses its `responses`/`index` state from the
      // previous module — `index` ends up out-of-bounds for the new
      // module's `visible` array and the body renders blank until the
      // user manually save-exits and resumes.
      key={currentModule.moduleKey}
      session={session}
      module={currentModule}
      progress={progress}
      pathType={pathType}
      legacyMigrationFlag={legacyMigrationFlag}
      discoveryInterestFlags={discoveryInterestFlags}
      onModuleComplete={async (responses) => {
        await apiPost(`/consumer/assessment/sessions/${sessionId}/modules/${currentModule.moduleKey}/complete`, { responses });
        // Sprint 3 handoff — when Discovery (Path A) finishes, jump to the
        // recommendation screen instead of marching into per-program
        // modules. Backend may also signal this via session.nextScreen
        // === "recommendation"; we honor either signal.
        const completedKey = currentModule.moduleKey;
        const refreshed = await apiGet(`/consumer/assessment/sessions/${sessionId}`).catch(() => null);
        const isDiscovery = completedKey === "discovery" || /discovery/i.test(currentModule.moduleName || "");
        const wantsRecommendation = isDiscovery || refreshed?.nextScreen === "recommendation";
        const alreadyHasSelection = (refreshed?.selectedPrograms?.length || 0) > 0;
        if (wantsRecommendation && !alreadyHasSelection) {
          navigate(`/app/assessment/${sessionId}/recommendation`);
          return;
        }
        setSession(refreshed);
      }}
      onSaveAndExit={async (responses) => {
        try {
          await apiPost(`/consumer/assessment/sessions/${sessionId}/responses`, { moduleKey: currentModule.moduleKey, responses });
          toast("Saved. Resume anytime from your home.", "success");
          navigate("/app", { state: { recentlySavedSessionId: sessionId } });
        } catch (err) {
          toast(err.detail || "Couldn't save your progress. Answers stay here — please try again.", "error");
        }
      }}
    />
  );
}

/* ============================================================= */
/*  ModuleRunner                                                  */
/* ============================================================= */
function ModuleRunner({ session, module, progress, pathType, legacyMigrationFlag, discoveryInterestFlags, onModuleComplete, onSaveAndExit }) {
  // Local response state for this module; pre-populated from session.responses
  // so save-and-resume restores prior answers. The backend has shipped at
  // least four shapes for `responses` over the life of this app — we accept
  // all of them so a member's answers always come back when they resume.
  const sessionId = session._id || session.id;
  const hydrateResponses = () => {
    const moduleKey = module.moduleKey;
    const out = {};
    const raw = session.responses;
    if (!raw) return out;

    const accept = (questionKey, candidateModuleKey, value) => {
      if (value === undefined) return;
      // Only keep responses that belong to this module. Some backend
      // shapes don't tag the moduleKey on each response — in that case
      // we accept the entry if its questionKey appears in this module's
      // schema (so we don't accidentally pull cross-module answers).
      if (candidateModuleKey && candidateModuleKey !== moduleKey) return;
      // The backend has been observed to double-wrap on save & resume:
      //   responses[k] = { value: { value: actual, moduleKey, ... }, ... }
      // Unwrap any number of nested `{ value, ... }` envelopes until we
      // reach the raw answer (string, number, array, or plain object
      // without a `value` field).
      let unwrapped = value;
      let safety = 0;
      while (
        unwrapped &&
        typeof unwrapped === "object" &&
        !Array.isArray(unwrapped) &&
        "value" in unwrapped &&
        safety < 5
      ) {
        unwrapped = unwrapped.value;
        safety += 1;
      }
      out[questionKey] = unwrapped;
    };

    // Build a quick lookup of this module's question keys.
    const moduleKeysInSchema = new Set((module.schema?.questions || []).map((q) => q.questionKey));

    // Shape 1: array of { questionKey, moduleKey, value }
    if (Array.isArray(raw)) {
      for (const r of raw) {
        if (!r || !r.questionKey) continue;
        accept(r.questionKey, r.moduleKey, r.value);
      }
      return out;
    }

    // Shape 2: dict keyed by moduleKey → { questionKey: value | {value} }
    if (raw[moduleKey] && typeof raw[moduleKey] === "object" && !raw[moduleKey].value) {
      for (const [qk, v] of Object.entries(raw[moduleKey])) {
        const value = v && typeof v === "object" && "value" in v ? v.value : v;
        accept(qk, moduleKey, value);
      }
      return out;
    }

    // Shape 3 & 4: dict keyed directly by questionKey → either
    //   { moduleKey, value, ... } (envelope), or
    //   the raw value itself.
    for (const [qk, v] of Object.entries(raw)) {
      if (v && typeof v === "object" && "value" in v) {
        // Envelope. Trust its moduleKey when present, otherwise infer
        // from the schema lookup so foreign-module answers don't leak.
        const candidate = v.moduleKey || (moduleKeysInSchema.has(qk) ? moduleKey : null);
        accept(qk, candidate, v.value);
      } else {
        // Raw value. Only adopt it if it belongs to this module's schema.
        if (moduleKeysInSchema.has(qk)) accept(qk, moduleKey, v);
      }
    }
    return out;
  };

  const [responses, setResponses] = useState(hydrateResponses);
  // Resume position. Walk the module's visible question list (after
  // applying conditional rules with the answers we just hydrated) and
  // start at the first one without an answer. Falls back to 0 when the
  // module is fresh, and to the last index when every question has an
  // answer (rare — usually the module would have been auto-completed).
  const [index, setIndex] = useState(() => {
    const all = module.schema?.questions || [];
    const initialResponses = hydrateResponses();
    const visibleAtMount = all.filter((q) => evaluateConditional(q, initialResponses));
    const firstUnanswered = visibleAtMount.findIndex((q) => {
      const v = initialResponses[q.questionKey];
      return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
    });
    if (firstUnanswered === -1) return Math.max(visibleAtMount.length - 1, 0);
    return firstUnanswered;
  });
  const [completing, setCompleting] = useState(false);

  const allQuestions = useMemo(() => module.schema?.questions || [], [module]);
  const visible = useMemo(
    () => allQuestions.filter((q) => evaluateConditional(q, responses)),
    [allQuestions, responses],
  );
  const current = visible[index];
  // Prefer the new BE progress envelope when present; fall back to
  // counting the legacy `session.modules` array. Both shapes produce
  // the same `completed / total` pair so the header/progress bar UI
  // doesn't need to know the difference.
  const completedModules = progress?.completed != null
    ? progress.completed
    : (session.modules || []).filter((m) => m.status === "completed").length;
  const totalModules = progress?.total != null
    ? progress.total
    : (session.modules || []).length;
  const currentModuleIndex = Math.min(completedModules, Math.max(totalModules - 1, 0));
  // Module-level roadmap source. Prefer the BE's progress.modules list
  // (which arrives with status + optional estimatedMinutes), fall back
  // to session.modules for older sessions, and finally synthesize a
  // single-entry list so the stepper never tries to render undefined.
  const roadmapModules = useMemo(() => {
    const src = (progress?.modules && progress.modules.length > 0)
      ? progress.modules
      : (session.modules || []);
    return src.map((m, i) => ({
      moduleKey: m.moduleKey,
      moduleName: m.moduleName || prettifyModuleKey(m.moduleKey),
      status: m.status
        || (i < completedModules ? "completed" : i === completedModules ? "in_progress" : "upcoming"),
      estimatedMinutes: m.estimatedMinutes || MODULE_ESTIMATES_MIN[m.moduleKey] || 5,
    }));
  }, [progress, session.modules, completedModules]);
  // Minutes-remaining estimate. Prefer BE-supplied
  // `progress.estimatedMinutesRemaining` when present; otherwise sum the
  // current + upcoming modules from our local estimate table.
  const remainingEstimateMin = useMemo(() => {
    if (typeof progress?.estimatedMinutesRemaining === "number") {
      return progress.estimatedMinutesRemaining;
    }
    return roadmapModules
      .slice(currentModuleIndex)
      .reduce((sum, m) => sum + (m.estimatedMinutes || 5), 0);
  }, [progress, roadmapModules, currentModuleIndex]);
  // Section transition surface — render a brief "Now we'll ask about…"
  // panel on the FIRST view of any non-universal program module in the
  // assessment-led path. Stored in localStorage so the panel only shows
  // once per module per session — refreshing the page doesn't re-show.
  const sectionIntro = useSectionIntro({ sessionId: session._id || session.id, moduleKey: module.moduleKey, pathType, totalModules });

  const setAnswer = (value) => {
    setResponses((prev) => ({ ...prev, [current.questionKey]: value }));
  };

  // Build the shaped envelope the backend expects. Used for both
  // incremental save (POST /responses) and module complete.
  const shapeAll = () => {
    const out = {};
    for (const k of Object.keys(responses)) {
      out[k] = { value: responses[k], moduleKey: module.moduleKey, answeredAt: new Date().toISOString() };
    }
    return out;
  };

  const next = async () => {
    if (!current) return;
    if (!isAnswerValid(current, responses[current.questionKey])) {
      toast(current.requiredMessage || "Please answer to continue.", "error");
      return;
    }
    // Persist the answer for this question before moving on. The backend
    // module-complete endpoint validates against *persisted* responses,
    // not the body sent on /complete, so without an incremental save the
    // final POST 422s with a "required answer missing" error for every
    // question that lived only in React state. We POST /responses on
    // every step transition (and again right before /complete) so the
    // server is always in sync with the member's answers.
    const shaped = shapeAll();
    try {
      await apiPost(`/consumer/assessment/sessions/${sessionId}/responses`, {
        moduleKey: module.moduleKey,
        responses: shaped,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[Assessment] incremental save failed (continuing)", err?.status, err?.detail);
      // Fall through — local state still holds the answer. If complete
      // also fails the user gets a clear toast then.
    }

    if (index < visible.length - 1) {
      setIndex(index + 1);
      return;
    }
    // Last question — complete the module.
    setCompleting(true);
    try {
      await onModuleComplete(shaped);
    } catch (err) {
      toast(err?.detail || `Couldn't save your answers (${err?.status || "?"}). Try again.`, "error");
    } finally {
      setCompleting(false);
    }
  };

  const back = () => { if (index > 0) setIndex(index - 1); };

  // Bail out of the question surface entirely while the section intro
  // is showing — keeps the framing dedicated, unbroken by question
  // chrome, until the member taps "Begin".
  if (sectionIntro.show) {
    return (
      <SectionIntro
        moduleName={module.moduleName || prettifyModuleKey(module.moduleKey)}
        moduleKey={module.moduleKey}
        sectionNumber={currentModuleIndex + 1}
        totalSections={totalModules}
        roadmapModules={roadmapModules}
        currentIndex={currentModuleIndex}
        remainingEstimateMin={remainingEstimateMin}
        onBegin={sectionIntro.dismiss}
      />
    );
  }

  return (
    <div data-testid="assessment-flow" style={{ minHeight: "100vh", background: C.ink, color: C.cream, display: "flex", flexDirection: "column" }}>
      <Header
        moduleName={module.moduleName || prettifyModuleKey(module.moduleKey)}
        completedModules={completedModules}
        totalModules={totalModules}
        currentIndex={currentModuleIndex}
        pathType={pathType}
        onSaveAndExit={() => {
          const shaped = {};
          for (const k of Object.keys(responses)) {
            shaped[k] = { value: responses[k], moduleKey: module.moduleKey, answeredAt: new Date().toISOString() };
          }
          onSaveAndExit(shaped);
        }}
      />
      <RoadmapStepper
        modules={roadmapModules}
        currentIndex={currentModuleIndex}
        remainingEstimateMin={remainingEstimateMin}
        pathType={pathType}
      />
      <ProgressBar current={completedModules + 1} total={totalModules || 1} />
      {/* Legacy migration banner — shown once per session when the BE
          signals a prior-program sunset (e.g. skin_program_sunset). */}
      {legacyMigrationFlag && (
        <LegacyMigrationBanner flag={legacyMigrationFlag} sessionId={session._id || session.id} />
      )}
      {/* Coming-soon interest banner — surfaced on the FIRST module
          step when the member tagged a not-yet-launched program in
          discovery. Physician sees the flags in review; this banner
          reassures the member those interests aren't lost. */}
      {Array.isArray(discoveryInterestFlags) && discoveryInterestFlags.length > 0 && index === 0 && (
        <InterestFlagsBanner flags={discoveryInterestFlags} />
      )}
      <div style={{ flex: 1, padding: "40px 22px 120px", maxWidth: 720, width: "100%", margin: "0 auto", boxSizing: "border-box" }}>
        {allQuestions.length === 0 && (
          <div data-testid="assessment-empty-state" style={{ padding: "32px 0", textAlign: "center" }}>
            <Eyebrow color={C.peach}>★ ONE MOMENT</Eyebrow>
            <h2 style={{ marginTop: 14, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: "clamp(28px, 5vw, 40px)", color: C.cream, lineHeight: 1.15 }}>
              Preparing your questions…
            </h2>
            <p style={{ marginTop: 16, fontSize: 14, color: "rgba(250,246,240,0.65)", lineHeight: 1.55, maxWidth: 420, margin: "16px auto 0" }}>
              We're loading your assessment. If this stays here, refresh — your answers are safe.
            </p>
          </div>
        )}
        {current && (
          <>
            <Eyebrow color={C.peach}>★ STEP {String(index + 1).padStart(2, "0")} / {String(visible.length).padStart(2, "0")}</Eyebrow>
            <h1
              style={{
                marginTop: 20,
                fontSize: "clamp(32px, 7vw, 56px)",
                fontWeight: 800,
                lineHeight: 1.05,
                letterSpacing: "-0.03em",
                textTransform: "uppercase",
                color: C.cream,
              }}
            >
              {current.questionText || current.label}
              {current.required === false && (
                <span data-testid="assessment-question-optional" style={{
                  marginLeft: 12,
                  fontSize: "0.32em",
                  fontWeight: 800,
                  letterSpacing: "0.22em",
                  color: C.peach,
                  verticalAlign: "middle",
                  textTransform: "uppercase",
                }}>
                  · OPTIONAL
                </span>
              )}
            </h1>
            {current.helpText && (
              <p style={{ marginTop: 14, fontSize: 14, color: "rgba(250,246,240,0.65)", lineHeight: 1.55, maxWidth: 560 }}>
                {current.helpText}
              </p>
            )}
            <div style={{ marginTop: 32 }}>
              <QuestionRenderer question={current} value={responses[current.questionKey]} onChange={setAnswer} />
            </div>
          </>
        )}
      </div>
      <FlowFooter
        onBack={index > 0 ? back : null}
        onNext={next}
        isLast={current && index === visible.length - 1}
        nextDisabled={completing || !current || !isAnswerValid(current, responses[current.questionKey])}
      />
    </div>
  );
}

function Header({ moduleName, completedModules, totalModules, currentIndex, pathType, onSaveAndExit }) {
  // Show "Section N of M" for assessment-led path, fall back to the
  // original "{module} · X / Y" framing for choice-led.
  const totalForLabel = totalModules || 1;
  const sectionNumber = (currentIndex != null ? currentIndex : completedModules) + 1;
  const label = pathType === "assessment_led"
    ? `SECTION ${sectionNumber} / ${totalForLabel} · ${(moduleName || "ASSESSMENT").toUpperCase()}`
    : `${(moduleName || "ASSESSMENT").toUpperCase()} · ${sectionNumber} / ${totalForLabel}`;
  return (
    <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px", borderBottom: `1px solid rgba(250,246,240,0.08)` }}>
      <Logo light size={20} />
      <div data-testid="assessment-header-label" style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.peach, textAlign: "center", flex: 1, padding: "0 12px" }}>
        {label}
      </div>
      <button
        type="button"
        data-testid="save-exit-btn"
        onClick={onSaveAndExit}
        style={{ background: "transparent", border: 0, color: "rgba(250,246,240,0.65)", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", cursor: "pointer" }}
      >
        SAVE & EXIT
      </button>
    </header>
  );
}

function FlowFooter({ onBack, onNext, isLast, nextDisabled }) {
  return (
    <footer
      style={{
        position: "sticky", bottom: 0,
        background: C.ink, borderTop: `1px solid rgba(250,246,240,0.1)`,
        padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
      }}
    >
      {onBack ? (
        <button
          type="button"
          data-testid="assessment-back-btn"
          onClick={onBack}
          style={{ background: "transparent", border: 0, color: "rgba(250,246,240,0.65)", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", cursor: "pointer" }}
        >
          ← BACK
        </button>
      ) : <span />}
      <Button variant="peach" size="lg" testId="assessment-next-btn" onClick={onNext} disabled={nextDisabled}>
        {isLast ? "FINISH MODULE →" : "CONTINUE →"}
      </Button>
    </footer>
  );
}

/* ============================================================= */
/*  RoadmapStepper — module-level progress timeline               */
/* ============================================================= */
//
// Compact horizontal stepper for multi-module assessments. Renders one
// segment per module (✓ completed · ● current · ○ upcoming) with a
// minutes-remaining estimate on the right. Tapping the bar expands a
// detailed list with each module's name, status, and per-module time
// estimate. Single-module sessions (choice-led path) render nothing.
function RoadmapStepper({ modules, currentIndex, remainingEstimateMin, pathType }) {
  const [expanded, setExpanded] = useState(false);
  if (!modules || modules.length <= 1) return null;

  // Only emphasize for assessment-led path; choice-led with a single
  // program module won't reach this branch anyway, but we keep the
  // pathType check explicit so the contract is clear.
  if (pathType && pathType !== "assessment_led" && modules.length <= 1) return null;

  const total = modules.length;
  const done = Math.max(0, Math.min(currentIndex, total));
  const remainingLabel = remainingEstimateMin > 0
    ? `~${remainingEstimateMin} MIN LEFT`
    : "ALMOST DONE";

  return (
    <div
      data-testid="assessment-roadmap"
      style={{
        background: "rgba(250,246,240,0.025)",
        borderBottom: `1px solid rgba(250,246,240,0.08)`,
      }}
    >
      <button
        type="button"
        data-testid="roadmap-toggle"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        aria-label={`Roadmap: section ${done + 1} of ${total}, ${remainingLabel.toLowerCase()}`}
        style={{
          width: "100%",
          background: "transparent",
          border: 0,
          padding: "12px 20px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          cursor: "pointer",
          color: C.cream,
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", gap: 6, alignItems: "center", flex: 1, minWidth: 0 }}>
          {modules.map((m, i) => {
            const status = i < currentIndex ? "completed" : i === currentIndex ? "current" : "upcoming";
            return <RoadmapSegment key={m.moduleKey || i} status={status} index={i} total={total} />;
          })}
        </div>
        <span
          data-testid="roadmap-remaining"
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.18em",
            color: C.peach,
            whiteSpace: "nowrap",
          }}
        >
          {remainingLabel}
        </span>
        <span aria-hidden style={{ color: "rgba(250,246,240,0.5)", fontSize: 11, marginLeft: 2 }}>
          {expanded ? "▴" : "▾"}
        </span>
      </button>
      {expanded && (
        <ul
          data-testid="roadmap-detail"
          style={{
            listStyle: "none",
            margin: 0,
            padding: "8px 20px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            borderTop: `1px solid rgba(250,246,240,0.06)`,
            background: "rgba(0,0,0,0.18)",
          }}
        >
          {modules.map((m, i) => {
            const status = i < currentIndex ? "completed" : i === currentIndex ? "current" : "upcoming";
            return (
              <li
                key={m.moduleKey || i}
                data-testid={`roadmap-item-${m.moduleKey || i}`}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "4px 0" }}
              >
                <RoadmapMarker status={status} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: status === "upcoming" ? "rgba(250,246,240,0.55)" : C.cream,
                      letterSpacing: "0.02em",
                      textTransform: "uppercase",
                    }}
                  >
                    {m.moduleName}
                    {status === "current" && (
                      <span style={{ marginLeft: 8, fontSize: 10, color: C.peach, letterSpacing: "0.18em" }}>
                        · IN PROGRESS
                      </span>
                    )}
                    {status === "completed" && (
                      <span style={{ marginLeft: 8, fontSize: 10, color: "rgba(250,246,240,0.5)", letterSpacing: "0.18em" }}>
                        · DONE
                      </span>
                    )}
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 11,
                    color: "rgba(250,246,240,0.55)",
                    fontFamily: '"Instrument Serif", Georgia, serif',
                    fontStyle: "italic",
                    whiteSpace: "nowrap",
                  }}
                >
                  ~{m.estimatedMinutes} min
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RoadmapSegment({ status, index, total }) {
  const isCurrent = status === "current";
  const isDone = status === "completed";
  // Connector line between segments (suppressed on the last one).
  return (
    <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
      <RoadmapMarker status={status} small />
      {index < total - 1 && (
        <div
          aria-hidden
          style={{
            flex: 1,
            height: 2,
            background: isDone
              ? C.peach
              : isCurrent
                ? "linear-gradient(90deg, " + C.peach + " 0%, rgba(250,246,240,0.18) 100%)"
                : "rgba(250,246,240,0.18)",
            backgroundImage: isCurrent
              ? `linear-gradient(90deg, ${C.peach} 0%, rgba(250,246,240,0.18) 100%)`
              : undefined,
            margin: "0 4px",
            minWidth: 8,
          }}
        />
      )}
    </div>
  );
}

function RoadmapMarker({ status, small }) {
  const size = small ? 14 : 22;
  const base = {
    width: size,
    height: size,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: small ? 9 : 12,
    fontWeight: 800,
    flexShrink: 0,
    transition: "all 120ms ease",
  };
  if (status === "completed") {
    return (
      <span style={{ ...base, background: C.peach, color: C.ink }} aria-label="completed">✓</span>
    );
  }
  if (status === "current") {
    return (
      <span
        style={{
          ...base,
          background: C.peach,
          color: C.ink,
          boxShadow: `0 0 0 ${small ? 3 : 4}px rgba(244,201,178,0.22)`,
        }}
        aria-label="current"
      >
        ●
      </span>
    );
  }
  return (
    <span
      style={{
        ...base,
        background: "transparent",
        border: `1.5px solid rgba(250,246,240,0.3)`,
        color: "rgba(250,246,240,0.5)",
      }}
      aria-label="upcoming"
    />
  );
}


/* ============================================================= */
/*  SubmitScreen — shown when every module is complete            */
/* ============================================================= */
function SubmitScreen({ session, onSubmitted }) {
  const navigate = useNavigate();
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);
  const [consent3, setConsent3] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pauseNotice, setPauseNotice] = useState(null);
  const sessionId = session._id || session.id;

  // Filter out empty/placeholder entries — the BE has been observed to
  // ship a session with `selectedPrograms: [{}]` on assessment-led
  // sessions, which used to render an empty bullet "· — · —/mo".
  // A program is "real" only if it has at least a name or programId.
  const programs = (session.selectedPrograms || []).filter(
    (p) => p && (p.programName || p.programId || p.programSlug),
  );
  const fees = session.fees || {};
  const total = fees.totalDue || fees.total || 0;
  // Path tagging: assessment-led members let the physician recommend
  // the program after submit; choice-led members ship the tray.
  const isAssessmentLed =
    session.pathType === "assessment_led" ||
    session.applicationType === "full_assessment";
  // The member usually pays the consult fee at checkout (Sprint 3 flow),
  // before they ever land on this screen — so by submit time the fee is
  // already settled. Detect every signal the backend has shipped for it.
  const isPrepaid =
    session.paymentReceived === true ||
    !!session.feeReceipt ||
    session.paymentStatus === "paid" ||
    session.paymentStatus === "succeeded" ||
    session.feeStatus === "paid";
  // Was this session expected to have a fee collected upstream?
  // YES for every session created under the Feb 2026 pricing contract
  // (both paths go through /app/checkout/:id before the assessment).
  // If the BE hasn't surfaced a payment receipt AND the fees envelope
  // is empty, that's a strong signal this is a legacy/dev-cart session
  // that slipped through without paying. We surface a recoverable
  // banner instead of letting the member submit at $0.
  const hasFeeData = !!(total || fees.initialConsult || fees.baseConsult);
  const feeExpectedButMissing = !isPrepaid && !hasFeeData;
  // The amount that was actually paid, in dollars. feeReceipt.total is in
  // cents (Stripe convention); fall back to fees.totalDue if the receipt
  // isn't on the session yet.
  const paidCents = session.feeReceipt?.total ?? session.amountPaid ?? null;
  const paidDollars = paidCents != null ? paidCents / 100 : (total || null);
  const canSubmit = consent1 && consent2 && consent3 && !submitting && !feeExpectedButMissing;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await apiPost(`/consumer/assessment/sessions/${sessionId}/submit`, {
        consents: { telehealth: true, accuracy: true, safety: true },
      });
      // If the submission triggered an auto-pause on an existing subscription,
      // surface the inline confirmation BEFORE navigating to the submitted page.
      if (res && (res.autoPauseTriggered || res.safetyPause)) {
        setPauseNotice(res.safetyPause || res.autoPauseDetails || { pausedSubscriptions: res.pausedSubscriptions || [] });
        setSubmitting(false);
        return;
      }
      await onSubmitted();
    } catch (err) {
      toast(err.detail || "Submit failed. Please try again.", "error");
      setSubmitting(false);
    }
  };

  if (pauseNotice) {
    return <AutoPauseInlineConfirm notice={pauseNotice} onContinue={onSubmitted} />;
  }

  return (
    <div data-testid="assessment-submit" style={{ minHeight: "100vh", background: C.cream, color: C.ink }}>
      <header style={{ padding: "18px 24px", borderBottom: `1px solid ${C.warmGray3}` }}>
        <Logo size={20} />
      </header>
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "64px 24px 120px" }}>
        <Eyebrow color={C.peachDeep}>★ FINAL STEP</Eyebrow>
        <Headline before="READY TO" italic="submit?" size={64} className="mt-4" />

        <Section title="APPLYING FOR">
          {programs.length === 0 ? (
            <p style={{ fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
              {isAssessmentLed
                ? "Your physician will recommend the right program based on your responses."
                : "Your physician will design the right plan based on your responses."}
            </p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
              {programs.map((p, i) => (
                <li key={p.programId || p.programSlug || i} style={{ fontSize: 15, color: C.ink }}>
                  · <strong>{p.programName || "Program"}</strong>
                  {p.tierName ? ` — ${p.tierName}` : ""}
                  {p.monthlyPrice ? ` · ${fmtMoney(p.monthlyPrice)}/mo` : ""}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {feeExpectedButMissing ? (
          <Section title="PAYMENT REQUIRED" testId="submit-fee-missing">
            <div
              data-testid="submit-fee-missing-banner"
              style={{
                background: C.bone,
                padding: 18,
                borderLeft: `3px solid ${C.coral}`,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.coral }}>
                ● PAYMENT NOT FOUND
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, lineHeight: 1.45 }}>
                We couldn't find a payment for this assessment.
              </div>
              <p style={{ fontSize: 13, color: C.warmGray, margin: 0, lineHeight: 1.6 }}>
                Reviews require a flat fee before a physician will read your responses. This usually means the session was started before our new checkout flow shipped — finish payment now and your answers will be sent for review.
              </p>
              <div style={{ marginTop: 4 }}>
                <Button
                  variant="primary"
                  testId="submit-go-to-checkout"
                  onClick={() => navigate(`/app/checkout/${sessionId}`)}
                >
                  GO TO CHECKOUT →
                </Button>
              </div>
            </div>
          </Section>
        ) : (
          <Section title={isPrepaid ? "PAYMENT" : "TODAY'S CHARGE"} testId="submit-fees">
            {isPrepaid ? (
              <div data-testid="submit-prepaid-confirmation" style={{ background: C.bone, padding: 18, borderLeft: `3px solid ${C.peachDeep}`, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
                  ● PHYSICIAN REVIEW PAID
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>
                  {paidDollars != null ? `${fmtMoney(paidDollars)} settled — nothing further today.` : "Settled — nothing further today."}
                </div>
                <p style={{ fontSize: 12, color: C.warmGray, margin: 0, lineHeight: 1.55 }}>
                  Each program is reviewed separately by a licensed physician. You'll only be charged a monthly subscription if approved and you choose to activate.
                </p>
              </div>
            ) : (
              <>
                <FeeRow label="Initial consult fee" value={fees.initialConsult || fees.baseConsult || 0} />
                {fees.additionalPrograms > 0 && (
                  <FeeRow label={`Additional programs (${programs.length > 1 ? programs.length - 1 : 0})`} value={fees.additionalPrograms} />
                )}
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 12, marginTop: 10, borderTop: `1.5px solid ${C.ink}` }}>
                  <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.18em", color: C.ink }}>TOTAL TODAY</span>
                  <strong style={{ fontSize: 20, color: C.ink, letterSpacing: "-0.02em" }}>{fmtMoney(total)}</strong>
                </div>
                <p style={{ marginTop: 14, fontSize: 12, color: C.warmGray, lineHeight: 1.6 }}>
                  Each program is reviewed separately by a licensed physician. You'll only be charged a monthly subscription if approved and you choose to activate.
                </p>
              </>
            )}
          </Section>
        )}

        <Section title="CONSENTS">
          <ConsentRow testId="consent-telehealth" checked={consent1} onToggle={() => setConsent1(!consent1)}>
            I understand that VOYA9 connects me with licensed physicians who provide medical care, and I consent to telehealth evaluation.{" "}
            <Link to="/legal/telehealth" target="_blank" style={{ color: C.peachDark }}>Read full consent →</Link>
          </ConsentRow>
          <ConsentRow testId="consent-accuracy" checked={consent2} onToggle={() => setConsent2(!consent2)}>
            I confirm my responses are accurate and complete to the best of my knowledge.
          </ConsentRow>
          <ConsentRow testId="consent-safety" checked={consent3} onToggle={() => setConsent3(!consent3)}>
            I understand my responses may trigger safety reviews of any active subscriptions if material health changes are reported.
          </ConsentRow>
        </Section>

        <div style={{ marginTop: 36 }}>
          <Button variant="primary" size="lg" block testId="submit-for-review-btn" onClick={submit} disabled={!canSubmit}>
            {submitting ? "SUBMITTING…" : "SUBMIT FOR PHYSICIAN REVIEW →"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, testId, children }) {
  return (
    <section data-testid={testId} style={{ marginTop: 36, paddingTop: 24, borderTop: `1px solid ${C.warmGray3}` }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 14 }}>{title}</div>
      {children}
    </section>
  );
}

function FeeRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 14 }}>
      <span style={{ color: C.warmGray }}>{label}</span>
      <span style={{ color: C.ink, fontWeight: 700 }}>{fmtMoney(value || 0)}</span>
    </div>
  );
}

function ConsentRow({ testId, checked, onToggle, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onToggle}
      style={{ width: "100%", display: "flex", gap: 14, background: "transparent", border: 0, padding: "10px 0", cursor: "pointer", textAlign: "left" }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0, width: 22, height: 22, border: `1.5px solid ${C.ink}`,
          background: checked ? C.ink : C.pure, color: C.cream, fontSize: 14, fontWeight: 800,
          display: "inline-flex", alignItems: "center", justifyContent: "center", marginTop: 2,
        }}
      >
        {checked ? "✓" : ""}
      </span>
      <span style={{ fontSize: 14, color: C.ink, lineHeight: 1.55 }}>{children}</span>
    </button>
  );
}

function AutoPauseInlineConfirm({ notice, onContinue }) {
  const paused = notice.pausedSubscriptions || notice.paused || [];
  const trigger = notice.triggerExplanation || notice.reason;
  return (
    <div data-testid="auto-pause-confirm" style={{ minHeight: "100vh", background: C.bone, color: C.ink }}>
      <header style={{ padding: "18px 24px", borderBottom: `1px solid ${C.warmGray3}` }}><Logo size={20} /></header>
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "72px 24px 120px" }}>
        <Eyebrow color={C.coral}>★ SAFETY REVIEW IN PROGRESS</Eyebrow>
        <Headline before="WE'VE" italic="paused your subscription." size={54} className="mt-4" />
        <p style={{ marginTop: 24, fontSize: 16, lineHeight: 1.65, color: C.ink }}>
          {trigger
            ? trigger
            : `Based on your responses, ${paused.length > 1 ? "your subscriptions are" : "your subscription is"} paused while your physician reviews. This is automatic and a safety measure.`}
        </p>
        {paused.length > 0 && (
          <ul style={{ marginTop: 20, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
            {paused.map((s, i) => (
              <li key={s._id || s.id || i} style={{ padding: "12px 14px", background: C.pure, border: `1.5px solid ${C.warmGray3}`, fontSize: 14 }}>
                · <strong>{s.programName || s.productName || "Subscription"}</strong>
                {s.tierName && <> — {s.tierName}</>}
              </li>
            ))}
          </ul>
        )}
        <p style={{ marginTop: 20, fontSize: 14, lineHeight: 1.6, color: C.warmGray }}>
          Your physician will review within 24 hours and reach out about next steps. You won't be charged while paused.
        </p>
        <div style={{ marginTop: 36 }}>
          <Button variant="primary" size="lg" block testId="auto-pause-ack-btn" onClick={onContinue}>
            ACKNOWLEDGE & CONTINUE →
          </Button>
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({ onRetry, onHome }) {  return (
    <div style={{ minHeight: "100vh", background: C.cream, color: C.ink, padding: "88px 24px" }}>
      <div style={{ maxWidth: 520, margin: "0 auto", textAlign: "center" }}>
        <Eyebrow color={C.coral}>★ WE HIT A SNAG</Eyebrow>
        <Headline before="LET'S TRY" italic="again." size={44} className="mt-4" />
        <p style={{ marginTop: 18, fontSize: 14, color: C.warmGray }}>
          We couldn't load your assessment session. This usually clears on a retry.
        </p>
        <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <Button variant="primary" testId="assessment-retry-btn" onClick={onRetry}>RETRY →</Button>
          <Button variant="ghost" testId="assessment-home-btn" onClick={onHome}>BACK TO HOME</Button>
        </div>
      </div>
    </div>
  );
}

// Payment gate — shown when a member lands on /app/assessment/:id for a
// session that has no payment receipt on file. Routes them to Checkout
// so the review fee is collected BEFORE they walk through the
// questionnaire. Their saved answers are preserved on the BE so payment
// finishes seamlessly and they resume in place.
function PaymentGate({ sessionId, onContinue, onHome }) {
  return (
    <div
      data-testid="assessment-payment-gate"
      style={{ minHeight: "100vh", background: C.cream, color: C.ink, padding: "88px 24px" }}
    >
      <div style={{ maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
        <Eyebrow color={C.peachDeep}>★ ONE LAST STEP BEFORE WE BEGIN</Eyebrow>
        <Headline before="FINISH" italic="payment." size={52} className="mt-4" />
        <p style={{ marginTop: 22, fontSize: 15, color: C.warmGray, lineHeight: 1.65 }}>
          We need to collect the review fee before a physician can read your responses. Your saved answers are safe — you'll resume right where you left off after payment.
        </p>
        <div style={{ marginTop: 32, display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <Button variant="primary" size="lg" testId="payment-gate-continue-btn" onClick={onContinue}>
            GO TO CHECKOUT →
          </Button>
          <Button variant="ghost" testId="payment-gate-home-btn" onClick={onHome}>
            BACK TO HOME
          </Button>
        </div>
        <p
          style={{
            marginTop: 28,
            fontSize: 12,
            color: C.warmGray,
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontStyle: "italic",
            lineHeight: 1.55,
          }}
          data-testid="payment-gate-session-id"
        >
          Session ID: {sessionId.slice(0, 8)}…
        </p>
      </div>
    </div>
  );
}

/* ============================================================= */
/*  Section intro + banners + helpers                              */
/* ============================================================= */

// Show the section intro panel once per (session, module) combination
// using localStorage. Universal-core and goals-discovery skip the
// panel — both modules are framed by the surrounding consent/discovery
// surfaces already. Choice-led path also skips (single program module,
// no "now we'll ask about…" reset needed).
function useSectionIntro({ sessionId, moduleKey, pathType, totalModules }) {
  const storageKey = sessionId && moduleKey ? `voya9.assessmentSectionIntro.${sessionId}.${moduleKey}` : null;
  const SKIP_MODULES = new Set(["universal_core", "goals_discovery"]);
  const eligible = pathType === "assessment_led"
    && totalModules > 1
    && moduleKey
    && !SKIP_MODULES.has(moduleKey);
  const [show, setShow] = useState(() => {
    if (!eligible || !storageKey) return false;
    try { return !localStorage.getItem(storageKey); } catch { return true; }
  });
  const dismiss = () => {
    if (storageKey) {
      try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    }
    setShow(false);
  };
  return { show, dismiss };
}

// Estimated minutes per module — purely informational, falls back to
// a generic phrase when the module isn't in the table. Keeps the
// member's expectations grounded without over-promising.
const MODULE_ESTIMATES_MIN = {
  weight_management: 4,
  recovery_healing: 6,
  sexual_wellness: 5,
  cognitive_performance: 5,
  longevity: 5,
  vitality_performance: 5,
};

function SectionIntro({ moduleName, moduleKey, sectionNumber, totalSections, roadmapModules, currentIndex, remainingEstimateMin, onBegin }) {
  const est = MODULE_ESTIMATES_MIN[moduleKey];
  const hasRoadmap = Array.isArray(roadmapModules) && roadmapModules.length > 1;
  return (
    <div data-testid="assessment-section-intro" style={{ minHeight: "100vh", background: C.ink, color: C.cream, display: "flex", flexDirection: "column" }}>
      <header style={{ padding: "18px 20px", borderBottom: `1px solid rgba(250,246,240,0.08)` }}>
        <Logo light size={20} />
      </header>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 22px" }}>
        <div style={{ maxWidth: 560, width: "100%" }}>
          <Eyebrow color={C.peach}>★ SECTION {sectionNumber} / {totalSections}</Eyebrow>
          <h1 style={{
            marginTop: 18,
            fontSize: "clamp(34px, 6.5vw, 52px)",
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: "-0.03em",
            textTransform: "uppercase",
            color: C.cream,
          }}>
            {(moduleName || "Next section").toUpperCase()}
          </h1>
          <p style={{ marginTop: 18, fontSize: 16, lineHeight: 1.6, color: "rgba(250,246,240,0.78)" }}>
            Now we’ll ask about your {sectionPurposeFor(moduleKey)}.
            {est ? ` About ${est}–${est + 2} minutes.` : ""}
          </p>
          <p style={{ marginTop: 8, fontSize: 13, lineHeight: 1.5, color: "rgba(250,246,240,0.55)", fontFamily: '"Instrument Serif", serif', fontStyle: "italic" }}>
            Take your time. Your answers save as you go.
          </p>
          {hasRoadmap && (
            <div
              data-testid="section-intro-roadmap"
              style={{
                marginTop: 28,
                paddingTop: 20,
                borderTop: `1px solid rgba(250,246,240,0.1)`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.peach }}>
                  YOUR ROADMAP
                </div>
                {remainingEstimateMin > 0 && (
                  <div style={{ fontSize: 11, color: "rgba(250,246,240,0.55)", fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
                    ~{remainingEstimateMin} min remaining total
                  </div>
                )}
              </div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                {roadmapModules.map((m, i) => {
                  const status = i < currentIndex ? "completed" : i === currentIndex ? "current" : "upcoming";
                  return (
                    <li
                      key={m.moduleKey || i}
                      data-testid={`section-intro-roadmap-item-${m.moduleKey || i}`}
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      <RoadmapMarker status={status} />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: status === "upcoming" ? "rgba(250,246,240,0.55)" : C.cream,
                            letterSpacing: "0.02em",
                            textTransform: "uppercase",
                          }}
                        >
                          {m.moduleName}
                          {status === "current" && (
                            <span style={{ marginLeft: 8, fontSize: 10, color: C.peach, letterSpacing: "0.18em" }}>
                              · NEXT
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          color: "rgba(250,246,240,0.55)",
                          fontFamily: '"Instrument Serif", Georgia, serif',
                          fontStyle: "italic",
                        }}
                      >
                        ~{m.estimatedMinutes} min
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <div style={{ marginTop: 32 }}>
            <button
              type="button"
              data-testid="assessment-section-begin"
              onClick={onBegin}
              style={{
                background: C.peach,
                color: C.ink,
                border: 0,
                padding: "16px 30px",
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "0.22em",
                cursor: "pointer",
                textTransform: "uppercase",
              }}
            >
              Begin section →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const SECTION_PURPOSE = {
  weight_management: "weight and appetite",
  recovery_healing: "recovery, healing and physical concerns",
  sexual_wellness: "sexual wellness — answers stay confidential",
  cognitive_performance: "cognitive function and mental performance",
  longevity: "longevity goals and cellular health",
  vitality_performance: "energy, vitality and hormonal goals",
};
function sectionPurposeFor(key) {
  return SECTION_PURPOSE[key] || "goals for this program";
}

function prettifyModuleKey(key) {
  if (!key) return "ASSESSMENT";
  return key.split(/[_-]+/).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");
}

// One-time banner shown when the BE flags a legacy module migration
// (e.g. Skin & Beauty program sunset). Dismissed per-session in
// localStorage so members aren't re-nagged on every page load.
function LegacyMigrationBanner({ flag, sessionId }) {
  const key = `voya9.assessmentLegacyBanner.${sessionId || "_"}.${flag}`;
  const [show, setShow] = useState(() => {
    try { return !localStorage.getItem(key); } catch { return true; }
  });
  if (!show) return null;
  const copy = LEGACY_FLAG_COPY[flag] || {
    title: "Program update",
    body: "The program you were exploring has been updated. Your physician will review your goals during your consultation.",
  };
  return (
    <div data-testid={`assessment-legacy-banner-${flag}`} style={{
      background: C.peach, color: C.ink,
      padding: "14px 20px", borderBottom: `1px solid rgba(0,0,0,0.06)`,
      display: "flex", alignItems: "flex-start", gap: 16,
    }}>
      <div style={{ flex: 1, fontSize: 13, lineHeight: 1.55 }}>
        <strong style={{ display: "block", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase" }}>★ {copy.title}</strong>
        <span>{copy.body}</span>
      </div>
      <button
        type="button"
        onClick={() => { try { localStorage.setItem(key, "1"); } catch { /* ignore */ } setShow(false); }}
        aria-label="Dismiss"
        style={{ background: "transparent", border: 0, color: C.ink, fontSize: 18, cursor: "pointer", padding: "0 4px", fontWeight: 700 }}
      >×</button>
    </div>
  );
}
const LEGACY_FLAG_COPY = {
  skin_program_sunset: {
    title: "Skin & Beauty has moved",
    body: "We’ve folded skin and collagen support into the Recovery & Healing program (GHK-Cu, GLOW, KLOW). Your physician will discuss the right fit during your consultation.",
  },
};

// Light callout listing the coming-soon programs a member tagged in
// discovery. Persistent (not dismissible) — sits below the legacy
// banner on the first step so members know their interest is logged.
function InterestFlagsBanner({ flags }) {
  const labels = flags.map((f) => INTEREST_FLAG_LABELS[f] || f).filter(Boolean);
  if (labels.length === 0) return null;
  return (
    <div data-testid="assessment-interest-flags" style={{
      background: "rgba(244,201,178,0.08)", color: C.cream,
      padding: "12px 20px", borderBottom: `1px solid rgba(250,246,240,0.06)`,
      fontSize: 12, lineHeight: 1.55,
    }}>
      <strong style={{ color: C.peach, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>★ Noted for review · </strong>
      <span style={{ color: "rgba(250,246,240,0.78)" }}>
        You also tagged interest in {labels.join(", ")} (coming soon). Your physician will see this and can discuss timing during your consultation.
      </span>
    </div>
  );
}
const INTEREST_FLAG_LABELS = {
  cognitive: "Cognitive Performance",
  longevity: "Longevity & Cellular Health",
  vitality: "Vitality & Performance",
};

/* ============================================================= */
/*  Submitted (route: /app/assessment/:id/submitted)              */
/* ============================================================= */
export function AssessmentSubmitted() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  useEffect(() => {
    apiGet(`/consumer/assessment/sessions/${sessionId}`).then(setSession).catch(() => setSession({}));
  }, [sessionId]);

  const physicianName = session?.assignedPhysician?.lastName ? `Dr. ${session.assignedPhysician.lastName}` : "your physician";

  return (
    <div data-testid="assessment-submitted" style={{ minHeight: "100vh", background: C.cream, color: C.ink }}>
      <header style={{ padding: "18px 24px", borderBottom: `1px solid ${C.warmGray3}` }}><Logo size={20} /></header>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "96px 24px 120px", textAlign: "center" }}>
        <div aria-hidden style={{ width: 76, height: 76, borderRadius: "50%", background: C.sage, color: C.ink, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 36, fontWeight: 800 }}>
          ✓
        </div>
        <div style={{ marginTop: 24 }}>
          <Eyebrow color={C.sageDark}>★ SUBMITTED</Eyebrow>
        </div>
        <Headline before="TO" italic={`${physicianName}.`} size={64} className="mt-4" />
        <p style={{ marginTop: 24, fontSize: 16, lineHeight: 1.6, color: C.warmGray, maxWidth: 460, margin: "24px auto 0" }}>
          Most reviews complete within 24 hours. You'll get notified the moment they respond.
        </p>
        <div style={{ marginTop: 40 }}>
          <Button variant="primary" testId="submitted-home-btn" onClick={() => navigate("/app", { replace: true })}>
            BACK TO HOME →
          </Button>
        </div>
      </div>
    </div>
  );
}
